import React, { useEffect, useState } from 'react';
import { bool } from 'prop-types';
import { compose } from 'redux';
import { connect, useDispatch, useSelector } from 'react-redux';

import { useConfiguration } from '../../context/configurationContext';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';

import { isScrollingDisabled } from '../../ducks/ui.duck';
import { loadWorkshopProduct, loadWorkshopHome } from '../../ducks/workshop.duck';
import { trackGA4Event } from '../../analytics/ga4';

import { Page, LayoutSingleColumn, NamedLink } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

import WorkshopSubnav from './WorkshopSubnav';
import css from './WorkshopPage.module.css';

// Affiliate coupon callouts, keyed by retailerLabel. Rendered just above the
// Buy button when the product's retailer has an active partner coupon. Kept as a
// small explicit map (not a CMS field) since only a handful of direct partners
// run codes; add a new entry here when a partner gives us one.
const COUPON_NOTES = {
  RideDog: 'Use code BIGLOADS for $50 off RideDog lifetime service plans.',
};

// Small inline orange checkmark (no emoji per brand rule).
const Check = () => (
  <svg
    className={css.pCheck}
    viewBox="0 0 20 20"
    fill="none"
    stroke="currentColor"
    strokeWidth={2.4}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M4 10.5l4 4 8-9" />
  </svg>
);

// Small neutral dot for "worth knowing" bullets (not a positive checkmark).
const Dot = () => (
  <svg
    className={css.pDot}
    viewBox="0 0 20 20"
    fill="currentColor"
    aria-hidden="true"
    focusable="false"
  >
    <circle cx="10" cy="10" r="3" />
  </svg>
);

// Parse the stored ownerSummary markdown back into { overview, likes[], watchOuts[] }.
// We write it in a fixed format (see the writer), so parsing is deterministic.
// Falls back gracefully to a plain overview if structure is missing.
function parseOwnerSummary(md) {
  if (!md || typeof md !== 'string') return null;
  const lines = md.split(/\r?\n/);
  const out = { overview: '', likes: [], watchOuts: [] };
  let section = 'overview';
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;
    if (/^#{1,6}\s*owners?\s+like/i.test(line)) { section = 'likes'; continue; }
    if (/^#{1,6}\s*(worth knowing|watch|good to know)/i.test(line)) { section = 'watch'; continue; }
    if (/^#{1,6}/.test(line)) { section = 'overview'; continue; }
    const bullet = line.replace(/^[-*]\s+/, '');
    const isBullet = /^[-*]\s+/.test(line);
    if (section === 'likes' && isBullet) out.likes.push(bullet);
    else if (section === 'watch' && isBullet) out.watchOuts.push(bullet);
    else if (section === 'overview') out.overview += (out.overview ? ' ' : '') + line;
  }
  if (!out.overview && !out.likes.length && !out.watchOuts.length) {
    out.overview = md.trim();
  }
  return out;
}

// Strip HTML and collapse whitespace.
const stripHtml = s => (s ? s.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim() : '');

// Truncate to <= max chars on a WORD boundary, add an ellipsis if clipped.
// Avoids the mid-word chop a raw .slice() produces.
const clampWords = (s, max) => {
  const t = (s || '').trim();
  if (t.length <= max) return t;
  const cut = t.slice(0, max);
  const lastSpace = cut.lastIndexOf(' ');
  const base = (lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut).replace(/[\s,.;:]+$/, '');
  return base + '\u2026';
};

// Turn a rich/plain description into a short set of "closer look" bullets.
// Prefers real sentences; falls back to nothing (we also show specTag bullets).
const sentenceBullets = (text, max = 3) => {
  const clean = stripHtml(text);
  if (!clean) return [];
  return clean
    .split(/(?<=[.!?])\s+/)
    .map(s => s.trim())
    .filter(s => s.length > 20 && s.length < 180)
    .slice(0, max);
};

const WorkshopProductPageComponent = props => {
  const { scrollingDisabled, config } = props;
  const dispatch = useDispatch();
  const params = useParams();
  const slug = params?.slug || '';

  const {
    currentProduct,
    relatedProducts,
    productInProgress,
    productError,
    categories,
  } = useSelector(state => state.workshop);

  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    dispatch(loadWorkshopProduct(slug));
    setActiveImg(0);
  }, [slug]); // eslint-disable-line

  useEffect(() => {
    if (!categories || categories.length === 0) dispatch(loadWorkshopHome());
  }, []); // eslint-disable-line

  const sortedCats = [...(categories || [])].sort((a, b) => (a.order || 0) - (b.order || 0));

  const marketplaceName = config?.marketplaceName || 'BigLoads';
  const siteUrl = config?.marketplaceRootURL || 'https://bigloads.com';

  if (productError && !currentProduct) {
    return <NotFoundPage />;
  }

  const p = currentProduct;
  const pageUrl = `${siteUrl}/trailer-accessories/product/${slug}`;

  const fireClick = () => {
    if (!p) return;
    trackGA4Event('click_affiliate', {
      item_id: p.asin || p.slug,
      item_name: p.name,
      item_brand: p.brand || undefined,
      item_category: p.category ? p.category.slug : undefined,
      price: p.priceValue || undefined,
      affiliate_network: p.affiliateNetwork || 'amazon',
      source: 'product_page',
    });
  };

  const seoTitle = p
    ? `${p.name} — Specs & Where to Buy | ${marketplaceName} Workshop`
    : `Trailer Accessory | ${marketplaceName}`;
  const seoDescription = p
    ? stripHtml(p.whyItMatters || p.description).slice(0, 155) ||
      `${p.name}, an owner-tested trailer accessory, spec-matched to your rig.`
    : '';

  // ── Structured data: Product + Offer + Breadcrumb ────────────────────────
  const schemas = [];
  if (p) {
    const productSchema = {
      '@type': 'Product',
      '@id': `${pageUrl}#product`,
      name: p.name,
      description: seoDescription,
      ...(p.image ? { image: [p.image] } : {}),
      ...(p.brand ? { brand: { '@type': 'Brand', name: p.brand } } : {}),
      ...(p.asin ? { sku: p.asin } : {}),
      ...(p.starRating && p.reviewCount
        ? {
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: p.starRating,
              reviewCount: p.reviewCount,
            },
          }
        : {}),
      ...(p.priceValue
        ? {
            offers: {
              '@type': 'Offer',
              price: p.priceValue,
              priceCurrency: 'USD',
              availability: 'https://schema.org/InStock',
              url: p.affiliateUrl,
            },
          }
        : {}),
    };
    const cat = p.category;
    const breadcrumbSchema = {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'The Workshop',
          item: `${siteUrl}/trailer-accessories`,
        },
        ...(cat
          ? [
              {
                '@type': 'ListItem',
                position: 3,
                name: cat.name,
                item: `${siteUrl}/trailer-accessories/category/${cat.slug}`,
              },
              { '@type': 'ListItem', position: 4, name: p.name, item: pageUrl },
            ]
          : [{ '@type': 'ListItem', position: 3, name: p.name, item: pageUrl }]),
      ],
    };
    schemas.push(productSchema, breadcrumbSchema);
  }

  // Derived content (only when product loaded).
  const cat = p && p.category;
  const images = p && p.image ? [p.image] : []; // most products carry one real image
  const specTags = (p && p.specTags) || [];
  const featureBoxes = specTags.slice(0, 3);
  const retailer = (p && p.retailerLabel) || 'Amazon';
  const couponNote = p && p.retailerLabel ? COUPON_NOTES[p.retailerLabel] : null;

  // "A closer look" bullets: real description sentences, else specTag labels.
  const descBullets = p ? sentenceBullets(p.whyItMatters || p.description, 4) : [];
  const closerBullets =
    descBullets.length >= 2
      ? descBullets
      : specTags.map(s => s.label).slice(0, 4);

  // Extra SEO paragraph for the "closer look" section. Built from real product
  // facts (name, category, trailer types, specs) so every page has unique,
  // keyword-relevant body copy without inventing claims.
  const seoParagraph = p ? buildSeoParagraph(p) : '';

  // Parsed "What owners say" structured summary (from Strapi ownerSummary markdown).
  const ownerSay = p ? parseOwnerSummary(p.ownerSummary) : null;

  // Spec table rows: use facet value when present, else fall back to the label.
  // Facet-backed tags become real label/value rows; plain tags (no facetKey)
  // collapse into a single "Features" row so we never print "Spec / Spec / Spec".
  const facetRows = specTags
    .filter(s => s.facetKey && s.facetValue)
    .map(s => ({ label: labelForFacet(s), value: s.facetValue }));
  const plainTags = specTags.filter(s => !(s.facetKey && s.facetValue)).map(s => s.label);
  const specRows = [
    ...facetRows,
    ...(plainTags.length ? [{ label: 'Features', value: plainTags.join(', ') }] : []),
  ];

  return (
    <Page
      className={css.root}
      scrollingDisabled={scrollingDisabled}
      contentType="website"
      title={seoTitle}
      description={seoDescription}
      canonicalUrl={pageUrl}
      schema={schemas}
    >
      <LayoutSingleColumn
        className={css.root}
        topbar={<TopbarContainer isWhiteBgNav={true} isHeaderSticky={true} />}
        footer={<FooterContainer />}
      >
        <WorkshopSubnav categories={sortedCats} css={css} />

        <div className={css.page}>
          {productInProgress && !p ? (
            <div className={css.collEmpty}>Loading the spec sheet…</div>
          ) : p ? (
            <>
              {/* Breadcrumb */}
              <nav className={css.breadcrumb} aria-label="Breadcrumb">
                <NamedLink name="LandingPage">Home</NamedLink>
                <span className={css.sep}>/</span>
                <NamedLink name="WorkshopPage">The Workshop</NamedLink>
                {cat ? (
                  <>
                    <span className={css.sep}>/</span>
                    <NamedLink name="WorkshopCategoryPage" params={{ catSlug: cat.slug }}>
                      {cat.name}
                    </NamedLink>
                  </>
                ) : null}
                <span className={css.sep}>/</span>
                <span className={css.current}>{p.name}</span>
              </nav>

              {/* Hero: gallery + info panel */}
              <section className={css.pHero}>
                <div className={css.pGallery}>
                  <div className={css.pMainImg}>
                    {images.length ? (
                      <img src={images[activeImg] || images[0]} alt={p.name} />
                    ) : (
                      <span className={css.ph}>{cat ? cat.name : 'Gear'}</span>
                    )}
                  </div>
                  {images.length > 1 ? (
                    <div className={css.pThumbs}>
                      {images.map((src, i) => (
                        <button
                          key={i}
                          type="button"
                          className={`${css.pThumb} ${i === activeImg ? css.pThumbOn : ''}`}
                          onClick={() => setActiveImg(i)}
                          aria-label={`View image ${i + 1}`}
                        >
                          <img src={src} alt="" />
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>

                <div className={css.pInfo}>
                  {p.kicker ? <div className={css.pKicker}>{p.kicker}</div> : null}
                  <h1 className={css.pTitle}>{p.name}</h1>
                  {p.brand ? <div className={css.pBrand}>by {p.brand}</div> : null}

                  {p.description || p.whyItMatters ? (
                    <p className={css.pShortDesc}>
                      {clampWords(stripHtml(p.description || p.whyItMatters), 220)}
                    </p>
                  ) : null}

                  {featureBoxes.length ? (
                    <div className={css.pGlance}>
                      <span className={css.pGlanceLabel}>At a glance</span>
                      <ul className={css.pGlanceList}>
                        {featureBoxes.map((f, i) => (
                          <li key={i} className={css.pGlanceItem}>
                            {f.label}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}

                  {couponNote ? (
                    <div className={css.pCoupon}>
                      <span className={css.pCouponTag}>Deal</span>
                      <span>{couponNote}</span>
                    </div>
                  ) : null}

                  {p.affiliateUrl ? (
                    <a
                      className={css.pBuyBtn}
                      href={p.affiliateUrl}
                      target="_blank"
                      rel="nofollow sponsored noopener noreferrer"
                      onClick={fireClick}
                    >
                      Buy on {retailer}
                    </a>
                  ) : null}

                  {ownerSay && ownerSay.likes.length ? (
                    <div className={css.pBuyHighlights}>
                      <div className={css.pBuyHighlightsHd}>Owners like</div>
                      <ul className={css.pBullets}>
                        {ownerSay.likes.slice(0, 3).map((b, i) => (
                          <li key={i}>
                            <Check />
                            <span>{b}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </div>
              </section>

              {/* Section anchor nav (SSR/SEO-safe, not JS tabs) */}
              <nav className={css.pSectionNav} aria-label="Product sections">
                <a href="#overview" className={css.pSectionLink}>
                  Overview
                </a>
                <a href="#specifications" className={css.pSectionLink}>
                  Specifications
                </a>
                {p.ownerSummary ? (
                  <a href="#what-owners-say" className={css.pSectionLink}>
                    What owners say
                  </a>
                ) : null}
              </nav>

              {/* A closer look */}
              <section id="overview" className={css.pSection}>
                <h2 className={css.pH2}>A closer look</h2>
                <div className={css.pCloserGrid}>
                  <div>
                    {p.whyItMatters ? (
                      <div
                        className={css.pProse}
                        dangerouslySetInnerHTML={{ __html: p.whyItMatters }}
                      />
                    ) : p.description ? (
                      <div
                        className={css.pProse}
                        dangerouslySetInnerHTML={{ __html: p.description }}
                      />
                    ) : null}
                    {closerBullets.length ? (
                      <ul className={css.pBullets}>
                        {closerBullets.map((b, i) => (
                          <li key={i}>
                            <Check />
                            <span>{b}</span>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                    {seoParagraph ? <p className={css.pProse}>{seoParagraph}</p> : null}
                  </div>
                  {images.length ? (
                    <div className={css.pCloserImg}>
                      <img src={images[0]} alt={p.name} loading="lazy" />
                    </div>
                  ) : null}
                </div>
              </section>

              {/* Specifications */}
              {specRows.length ? (
                <section id="specifications" className={css.pSection}>
                  <h2 className={css.pH2}>Product specifications</h2>
                  <div className={css.pSpecGrid}>
                    <table className={css.pSpecTable}>
                      <tbody>
                        {specRows.map((row, i) => (
                          <tr key={i}>
                            <th scope="row">{row.label}</th>
                            <td>{row.value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className={css.pSpecNote}>
                      Specifications are compiled from the manufacturer listing. Confirm exact
                      dimensions and ratings on {retailer} before you order.
                    </div>
                  </div>
                </section>
              ) : null}

              {/* What owners say (AI-summarized public owner sentiment) */}
              {ownerSay ? (
                <section id="what-owners-say" className={css.pSection}>
                  <h2 className={css.pH2}>What owners say</h2>
                  <div className={css.pOwnerCard}>
                    {ownerSay.overview ? (
                      <p className={css.pOwnerOverview}>{ownerSay.overview}</p>
                    ) : null}
                    <div className={css.pOwnerCols}>
                      {ownerSay.likes.length ? (
                        <div className={css.pOwnerCol}>
                          <div className={css.pOwnerColHd}>Owners like</div>
                          <ul className={css.pBullets}>
                            {ownerSay.likes.map((b, i) => (
                              <li key={i}>
                                <Check />
                                <span>{b}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                      {ownerSay.watchOuts.length ? (
                        <div className={css.pOwnerCol}>
                          <div className={css.pOwnerColHd}>Worth knowing</div>
                          <ul className={`${css.pBullets} ${css.pBulletsNeutral}`}>
                            {ownerSay.watchOuts.map((b, i) => (
                              <li key={i}>
                                <Dot />
                                <span>{b}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                    </div>
                    <p className={css.pOwnerDisclosure}>
                      Summarized from publicly available owner reviews and discussion across the
                      web. Confirm current details on the retailer listing.
                    </p>
                  </div>
                </section>
              ) : null}

              {/* Related gear */}
              {relatedProducts && relatedProducts.length ? (
                <section className={css.pSection}>
                  <h2 className={css.pH2}>Related gear</h2>
                  <div className={css.pRelatedGrid}>
                    {relatedProducts.map(r => (
                      <article key={r.slug} className={css.collCard}>
                        <NamedLink
                          name="WorkshopProductPage"
                          params={{ slug: r.slug }}
                          className={css.collCardImg}
                        >
                          {r.image ? (
                            <img src={r.image} alt={r.name} loading="lazy" />
                          ) : (
                            <span className={css.ph} style={{ minHeight: 0 }}>
                              {r.category ? r.category.name : 'Gear'}
                            </span>
                          )}
                        </NamedLink>
                        <div className={css.collCardBody}>
                          {r.kicker || r.category ? (
                            <div className={css.collCardKicker}>
                              {(r.kicker || (r.category && r.category.name) || '').toString()}
                            </div>
                          ) : null}
                          <h3 className={css.collCardName}>
                            <NamedLink name="WorkshopProductPage" params={{ slug: r.slug }}>
                              {r.name}
                            </NamedLink>
                          </h3>
                          <div className={css.collCardFoot}>
                            <NamedLink
                              name="WorkshopProductPage"
                              params={{ slug: r.slug }}
                              className={css.collCardView}
                            >
                              View details <span className={css.arrow}>→</span>
                            </NamedLink>
                          </div>
                        </div>
                      </article>
                    ))}
                  </div>
                </section>
              ) : null}
            </>
          ) : (
            <div className={css.collEmpty}>Product not found.</div>
          )}
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

// Build a unique, keyword-relevant SEO paragraph from real product facts.
// No invented claims: only name, category, trailer-type fitment, and spec labels.
function buildSeoParagraph(p) {
  const cat = p.category ? p.category.name.toLowerCase() : 'trailer gear';
  const types = (p.trailerTypes || []).map(t => t.name.toLowerCase());
  const specLabels = (p.specTags || []).map(s => s.label).filter(Boolean);

  const fitSentence = types.length
    ? `BigLoads lists it under ${cat} because it fits the way ${
        types.length > 1
          ? `${types.slice(0, -1).join(', ')} and ${types[types.length - 1]} owners`
          : `${types[0]} owners`
      } actually load and secure a trailer.`
    : `BigLoads lists it under ${cat} so you can match it to the trailer you run.`;

  const specSentence = specLabels.length
    ? `Key details to weigh: ${specLabels.slice(0, 4).join(', ')}.`
    : '';

  const closer = `If it is the right fit for your setup, you can buy ${
    p.name
  } direct from ${p.retailerLabel || 'Amazon'} at the same price, and BigLoads earns a small commission that keeps the owner guides free.`;

  return [fitSentence, specSentence, closer].filter(Boolean).join(' ');
}

// Human-readable label for a known facetKey; falls back to the tag label.
function labelForFacet(s) {
  const map = {
    wll: 'Working load limit (lb)',
    length: 'Length',
    width: 'Width',
    capacity: 'Capacity',
    diameter: 'Diameter',
    size: 'Size',
    material: 'Material',
    gauge: 'Gauge',
    voltage: 'Voltage',
    pieces: 'Quantity',
    pack: 'Pack size',
  };
  return map[s.facetKey] || s.label;
}

WorkshopProductPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
};

const EnhancedWorkshopProductPage = props => {
  const config = useConfiguration();
  return <WorkshopProductPageComponent config={config} {...props} />;
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

const WorkshopProductPage = compose(connect(mapStateToProps))(EnhancedWorkshopProductPage);

// SSR loader (named export for pageDataLoadingAPI)
export const loadData = (params, search, config) => dispatch => {
  const slug = params?.slug || '';
  return dispatch(loadWorkshopProduct(slug));
};

export default WorkshopProductPage;
